// Kinematic walking character on a VoxelGrid.
//
// The body is a vertical cylinder that starts `stepHeight` above the feet and reaches `height`: the
// lowest part is left to a separate ground probe, as in game character controllers. That way floor
// unevenness, voxel rounding, door sills and single stairs never block the body; anything taller than
// a step (furniture, walls) does.
//
// Safety layers, in order: sub-stepping (never more than half a voxel per step, so no tunnelling at
// low frame rates), wall sliding, ground snap up and down within one step, a post-step overlap check
// that rejects any step ending in geometry, depenetration on spawn/teleport, and breadcrumbs of safe
// positions to return to if the ground ever disappears (holes in the scan).

const EPS = 1e-4;

export const DEFAULT_AGENT = {
    radius: 0.18,       // smaller than the carve radius: every carved doorway stays passable
    height: 1.45,       // smaller than the carve height
    eyeHeight: 1.6,     // may exceed the body; lowered automatically under low ceilings
    stepHeight: 0.25,   // sills and single stairs; tables (0.7 m) stay out of reach
    gravity: 9.81,
    maxFallSpeed: 6,
    maxFallDistance: 1.2 // falling further than this means a hole in the scan: go back to safety
};

export class Character {
    /**
     * @param {import('./voxel-grid.js').VoxelGrid} grid
     * @param {object} [agent]
     */
    constructor(grid, agent = {}) {
        this.grid = grid;
        this.agent = { ...DEFAULT_AGENT, ...agent };
        this.pos = [0, 0, 0];          // feet position
        this.vy = 0;
        this.onGround = false;
        this.fallStart = null;
        this.safe = [];                // breadcrumb of recent safe feet positions
        this.safeTimer = 0;
        this.resets = 0;               // how often the safety net had to act (QA metric)
        this.skin = Math.min(0.02, grid.res * 0.4);
    }

    /** True if the body (above step height) at feet position (x, feetY, z) intersects solid voxels. */
    overlaps(x, feetY, z) {
        const g = this.grid, r = this.agent.radius, res = g.res;
        const x0 = g.ix(x - r), x1 = g.ix(x + r), z0 = g.iz(z - r), z1 = g.iz(z + r);
        const y0 = g.iy(feetY + this.agent.stepHeight + this.skin), y1 = g.iy(feetY + this.agent.height);
        const r2 = r * r;
        for (let iz = z0; iz <= z1; iz++) {
            // Distance from the axis to the voxel's square footprint
            const vz0 = g.min[2] + iz * res;
            const dz = z < vz0 ? vz0 - z : z > vz0 + res ? z - vz0 - res : 0;
            for (let ix = x0; ix <= x1; ix++) {
                const vx0 = g.min[0] + ix * res;
                const dx = x < vx0 ? vx0 - x : x > vx0 + res ? x - vx0 - res : 0;
                if (dx * dx + dz * dz >= r2) continue;
                for (let iy = y0; iy <= y1; iy++) {
                    if (g.solid(ix, iy, iz)) return true;
                }
            }
        }
        return false;
    }

    /**
     * Highest solid top under the footprint at or below maxY, no lower than maxY - drop.
     * Samples the centre and four points at 60 % radius, so the character neither hangs on edges nor
     * drops into gaps narrower than its feet. Returns null if there is no ground in range.
     */
    groundBelow(x, z, maxY, drop) {
        const g = this.grid, k = this.agent.radius * 0.6;
        const top = g.iy(maxY - EPS), bottom = g.iy(maxY - drop);
        let best = null;
        const samples = [[0, 0], [k, 0], [-k, 0], [0, k], [0, -k]];
        for (const [ox, oz] of samples) {
            const ix = g.ix(x + ox), iz = g.iz(z + oz);
            for (let iy = top; iy >= bottom; iy--) {
                if (g.solid(ix, iy, iz)) {
                    const h = g.top(iy);
                    if (best === null || h > best) best = h;
                    break;
                }
            }
        }
        return best;
    }

    /** Feet position is valid: body free and something to stand on within reach of the feet. */
    _standable(x, y, z) {
        return !this.overlaps(x, y, z);
    }

    /**
     * Place the character at or near (x, y, z): find the floor below, then push out of any overlap by
     * searching outward in rings. Returns false if no free spot was found within `searchRadius`.
     */
    spawn(x, y, z, searchRadius = 1.5) {
        const res = this.grid.res;
        const tryAt = (px, pz) => {
            const ground = this.groundBelow(px, pz, y + this.agent.stepHeight, 4);
            if (ground === null || !this._standable(px, ground, pz)) return false;
            this.pos = [px, ground, pz];
            return true;
        };
        let ok = tryAt(x, z);
        for (let ring = 1; !ok && ring * res <= searchRadius; ring++) {
            const n = Math.max(8, Math.round(ring * 6.3));
            for (let i = 0; i < n && !ok; i++) {
                const a = (i / n) * Math.PI * 2;
                ok = tryAt(x + Math.cos(a) * ring * res, z + Math.sin(a) * ring * res);
            }
        }
        this.vy = 0;
        this.onGround = ok;
        this.fallStart = null;
        if (ok) this.safe = [this.pos.slice()];
        return ok;
    }

    /** One horizontal sub-step to (nx, nz) including ground follow. Returns true if it moved. */
    _step(nx, nz) {
        const [, y] = this.pos;
        if (this.overlaps(nx, y, nz)) return false;
        if (this.onGround) {
            // Follow the floor within one step up or down; the body above must still fit there
            const a = this.agent;
            const ground = this.groundBelow(nx, nz, y + a.stepHeight + this.skin, 2 * a.stepHeight + this.skin);
            if (ground !== null) {
                if (ground > y && this.overlaps(nx, ground, nz)) return false;
                this.pos[0] = nx; this.pos[1] = ground; this.pos[2] = nz;
                return true;
            }
            // No floor within a step: walk off the edge and fall
            this.onGround = false;
            this.fallStart = y;
            this.vy = 0;
        }
        this.pos[0] = nx; this.pos[2] = nz;
        return true;
    }

    /**
     * Advance by a desired horizontal displacement (dx, dz) over dt seconds.
     * @returns {{blocked:boolean, reset:boolean}}
     */
    move(dx, dz, dt) {
        const a = this.agent, g = this.grid;
        let blocked = false, reset = false;

        // Already stuck (teleport into geometry, data swap): recover before moving
        if (this.overlaps(this.pos[0], this.pos[1], this.pos[2])) {
            if (!this.spawn(this.pos[0], this.pos[1], this.pos[2], 0.6)) this._reset();
            return { blocked: true, reset: true };
        }

        if (!this.onGround) this.vy = Math.max(this.vy - a.gravity * dt, -a.maxFallSpeed);
        const dy = this.vy * dt;
        const maxStep = g.res * 0.45;
        const n = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dz), Math.abs(dy)) / maxStep));
        const sx = dx / n, sz = dz / n, sy = dy / n;

        for (let i = 0; i < n; i++) {
            if (sx !== 0 || sz !== 0) {
                const [x, , z] = this.pos;
                if (!this._step(x + sx, z + sz)) {
                    blocked = true;
                    // Slide along the wall: keep whichever component still fits
                    if (!(sx !== 0 && this._step(x + sx, z)) && sz !== 0) this._step(x, z + sz);
                }
            }
            if (!this.onGround) {
                const [x, y, z] = this.pos;
                const ny = y + sy;
                const ground = this.groundBelow(x, z, y + this.skin, y - ny + this.skin);
                if (ground !== null && ground >= ny) {
                    this.pos[1] = ground;
                    this.onGround = true;
                    this.vy = 0;
                    this.fallStart = null;
                } else {
                    this.pos[1] = ny;
                }
            }
        }

        if (!this.onGround && this.fallStart !== null &&
            (this.fallStart - this.pos[1] > a.maxFallDistance || this.pos[1] < g.min[1])) {
            this._reset();
            reset = true;
        }

        // Breadcrumbs: remember safe positions a few times per second
        this.safeTimer += dt;
        if (this.onGround && this.safeTimer > 0.25) {
            this.safeTimer = 0;
            const last = this.safe[this.safe.length - 1];
            if (!last || Math.hypot(last[0] - this.pos[0], last[2] - this.pos[2]) > 0.3) {
                this.safe.push(this.pos.slice());
                if (this.safe.length > 40) this.safe.shift();
            }
        }
        return { blocked, reset };
    }

    _reset() {
        this.resets++;
        // Walk back through the breadcrumbs until one is still valid
        while (this.safe.length) {
            const p = this.safe.pop();
            if (this._standable(p[0], p[1], p[2]) && this.groundBelow(p[0], p[2], p[1] + this.skin, 0.1) !== null) {
                this.pos = p.slice();
                this.safe.push(p);
                this.vy = 0;
                this.onGround = true;
                this.fallStart = null;
                return;
            }
        }
        this.vy = 0;
        this.fallStart = null;
    }

    /** Teleport with collision checks (room jumps). Keeps the old position if the target is blocked. */
    teleport(x, y, z) {
        const prev = { pos: this.pos.slice(), safe: this.safe.slice() };
        if (this.spawn(x, y, z, 0.8)) return true;
        this.pos = prev.pos;
        this.safe = prev.safe;
        this.onGround = true;
        return false;
    }

    /** Free height above the feet (m), up to `limit`. */
    headroom(limit = 3) {
        const g = this.grid, [x, y, z] = this.pos;
        const ix = g.ix(x), iz = g.iz(z);
        for (let iy = g.iy(y + this.agent.stepHeight); g.top(iy) - y < limit; iy++) {
            if (g.solid(ix, iy, iz)) return g.min[1] + iy * g.res - y;
        }
        return limit;
    }

    /** Eye position, lowered under sloped or low ceilings so the camera stays inside the room. */
    get eye() {
        const h = Math.min(this.agent.eyeHeight, this.headroom(this.agent.eyeHeight + 0.2) - 0.12);
        return [this.pos[0], this.pos[1] + Math.max(h, this.agent.height * 0.8), this.pos[2]];
    }
}
